import { SecurityPermissions, UserSessionRequest } from './authService';

export type User = {
  first_name: string;
  last_name: string;
  email_address: string;
  source_account_id: string;
  source_user_id: string; // Now required with hardcoded value
  timezone_hours_offset: number;
  security_permissions: SecurityPermissions;
};

export type Product = {
  id: string;
  name: string;
  productKey: string;
  users: User[];
};

// TeamConnect specific permissions
const teamConnectPermissions: SecurityPermissions = {
  row_level_permissions: [
    {
      logical_groups: {
        logical_group: [
          {
            id: 'teamconnect-group',
            restricted: 'false',
            matter_ids: ['1', '2', '3'],
          },
        ],
      },
    },
  ],
};

// TAP specific permissions
const tapPermissions: SecurityPermissions = {
  row_level_permissions: [
    {
      logical_groups: {
        logical_group: [
          {
            id: 'tap-group',
            restricted: 'false',
            matter_ids: ['4', '5', '6'],
          },
        ],
      },
    },
  ],
};

const TEAM_CONNECT_SOURCE_ACCOUNT_ID = 'UAT_SANDBOX';
// const TAP_SOURCE_ACCOUNT_ID = '33EF5DFB-0483-4890-A0AF-56FF39731973';

const getRequestBodyForTAP = (user: User): UserSessionRequest => ({
  data: {
    type: 'users',
    attributes: {
      first_name: user.first_name ?? 'John',
      last_name: user.last_name ?? 'Staging',
      email_address: user.email_address ?? 'testing.testing@mitratech.com',
      source_user_id: user.source_user_id ?? 'de51e8d5-9429-4a4e-b26a-a856769ce659',
      source_account_id: user.source_account_id ?? '33ef5dfb-0483-4890-a0af-56ff39731973',
      timezone_hours_offset: 0,
      security_permissions: {
        user_access: {
          logical_operators: 'OR',
          roles: ['*'],
          departments: ['*'],
        },
      },
    },
  },
});

const getRequestBodyForTeamConnect = (user: User): UserSessionRequest => ({
  data: {
    type: 'users',
    attributes: {
      first_name: user.first_name ?? 'John',
      last_name: user.last_name ?? 'Staging',
      email_address: user.email_address ?? 'test.test@mitratech.com',
      source_account_id: 'UAT_SANDBOX',
      source_user_id: user.source_user_id ?? '3',
      timezone_hours_offset: user.timezone_hours_offset ?? 12,
      security_permissions: {
        row_level_permissions: [
          {
            logical_groups: {
              logical_group: [{ id: 'abc', restricted: 'false', matter_ids: ['1'] }],
            },
          },
        ],
      },
    },
  },
});

const getRequestBody = (user: User, productKey: string): UserSessionRequest => {
  if (productKey === import.meta.env.VITE_STAGING_DATA_HUB_PRODUCT_KEY_TAP) {
    return getRequestBodyForTAP(user);
  } return getRequestBodyForTeamConnect(user);
};

export const products: Product[] = [
  {
    id: 'teamconnect',
    name: 'TeamConnect',
    productKey: import.meta.env.VITE_STAGING_DATA_HUB_PRODUCT_KEY_TC,
    users: [
      {
        first_name: 'Manu',
        last_name: 'TeamConnect',
        email_address: 'manu@mitratech.com',
        source_account_id: TEAM_CONNECT_SOURCE_ACCOUNT_ID,
        source_user_id: '1503',
        timezone_hours_offset: 8,
        security_permissions: teamConnectPermissions,
      },
      {
        first_name: 'Harsh',
        last_name: 'TeamConnect',
        email_address: 'harsh@mitratech.com',
        source_account_id: TEAM_CONNECT_SOURCE_ACCOUNT_ID,
        source_user_id: '7513',
        timezone_hours_offset: 5,
        security_permissions: teamConnectPermissions,
      },
    ],
  },
  {
    id: 'tap',
    name: 'TAP',
    productKey: import.meta.env.VITE_STAGING_DATA_HUB_PRODUCT_KEY_TAP,
    users: [
      {
        first_name: 'Robert',
        last_name: 'TAP',
        email_address: 'robert.tap@mitratech.com',
        source_account_id: '33EF5DFB-0483-4890-A0AF-56FF39731973',
        source_user_id: 'robert-tap-789',
        timezone_hours_offset: 6,
        security_permissions: tapPermissions,
      },
      {
        first_name: 'Susan',
        last_name: 'TAP',
        email_address: 'susan.tap@mitratech.com',
        source_account_id: 'BC7FAC73-00EC-4153-8CD3-7CA90E45CFD2',
        source_user_id: 'susan-tap-abc',
        timezone_hours_offset: 7,
        security_permissions: tapPermissions,
      },
    ],
  },
];

// No longer needed - using hardcoded user IDs instead

// Function to create request body from selected product and user
export function createUserRequestBody(
  productId: string,
  userName: string
): { requestBody: UserSessionRequest; productKey: string } | undefined {
  // Find the product
  const product = products.find((p) => p.id === productId);
  if (!product) return undefined;

  // Find the user
  const user = product.users.find((u) => `${u.first_name} ${u.last_name}` === userName);
  if (!user) return undefined;

  // Use the hardcoded user ID and return both request body and product key
  return {
    requestBody: getRequestBody(user, product.productKey),
    productKey: product.productKey,
  };
}
